import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, Video, Image as ImageIcon, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { MediaField } from "@/components/admin/MediaField";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listAdminVideoSpecials,
  createVideoSpecial,
  updateVideoSpecial,
  deleteVideoSpecial,
  reorderVideoSpecials,
  type AdminVideoSpecial,
} from "@/lib/admin/video-specials.functions";
import { listAdminProducts } from "@/lib/admin/products.functions";
// Note: We need a way to list mp2_dishes specifically. 
// Re-using listAdminProducts might work if they are related, but mp2_dishes is what we need.
// Let's check if there's an existing mp2 dish list function.
import { listActiveProducts } from "@/lib/cms/products.functions"; 
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_admin/admin/video-specials")({
  component: VideoSpecialsAdminPage,
});

function VideoSpecialsAdminPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAdminVideoSpecials);
  const createFn = useServerFn(createVideoSpecial);
  const updateFn = useServerFn(updateVideoSpecial);
  const deleteFn = useServerFn(deleteVideoSpecial);
  const reorderFn = useServerFn(reorderVideoSpecials);

  const { data: specials, isLoading } = useQuery({
    queryKey: ["admin", "video-specials"],
    queryFn: () => listFn(),
  });

  // We need to fetch dishes for the selector
  const { data: dishes } = useQuery({
    queryKey: ["admin", "mp2-dishes-selector"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mp2_dishes")
        .select("id, name, alacarte_price_paise, is_active")
        .order("name");
      if (error) throw error;
      return data;
    }
  });

  const [editing, setEditing] = useState<AdminVideoSpecial | "new" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminVideoSpecial | null>(null);
  const [form, setForm] = useState({
    dish_id: "",
    video_url: "",
    poster_url: "",
    sales_description: "",
    is_active: true,
  });

  useEffect(() => {
    if (editing === "new") {
      setForm({
        dish_id: "",
        video_url: "",
        poster_url: "",
        sales_description: "",
        is_active: true,
      });
    } else if (editing) {
      setForm({
        dish_id: editing.dish_id,
        video_url: editing.video_url || "",
        poster_url: editing.poster_url,
        sales_description: editing.sales_description || "",
        is_active: editing.is_active,
      });
    }
  }, [editing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form.dish_id || !form.poster_url) {
        throw new Error("Missing required fields");
      }
      const payload = { ...form };
      if (editing === "new") {
        return createFn({ data: payload });
      } else if (editing) {
        return updateFn({ data: { id: editing.id, patch: payload } });
      }
    },
    onSuccess: () => {
      toast.success("Saved successfully");
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "video-specials"] });
      queryClient.invalidateQueries({ queryKey: ["video-specials", "public"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to save");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "video-specials"] });
    }
  });

  const move = async (index: number, dir: number) => {
    if (!specials) return;
    const items = [...specials];
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    
    const [moved] = items.splice(index, 1);
    items.splice(target, 0, moved);
    
    try {
      await reorderFn({ data: { ids: items.map(i => i.id) } });
      queryClient.invalidateQueries({ queryKey: ["admin", "video-specials"] });
    } catch (err) {
      toast.error("Reorder failed");
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader 
        title="Video Sales / Today's Specials" 
        description="Manage high-impact video cards for today's featured dishes."
      />

      <div className="flex justify-between items-center">
        <Badge variant="outline" className="text-muted-foreground">
          {specials?.filter(s => s.is_active).length || 0} / 6 Active
        </Badge>
        <Button onClick={() => setEditing("new")} className="gap-2">
          <Plus className="h-4 w-4" /> Add Video Card
        </Button>
      </div>

      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 w-20">Order</th>
              <th className="p-3">Dish</th>
              <th className="p-3">Video / Poster</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {specials?.map((special, i) => (
              <tr key={special.id} className="border-t">
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)} disabled={i === 0}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)} disabled={i === (specials.length - 1)}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
                <td className="p-3">
                  <div className="font-medium">{special.dish?.name}</div>
                  <div className="text-xs text-muted-foreground">
                    ₹{(special.dish?.alacarte_price_paise || 0) / 100}
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-8 bg-black rounded overflow-hidden relative group">
                      <img src={special.poster_url} className="h-full w-full object-cover opacity-60" />
                      <PlayCircle className="h-4 w-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
                    </div>
                    <div className="text-xs truncate max-w-[200px] text-muted-foreground">
                      {special.video_url ? special.video_url.split('/').pop() : "Using dish image — no video uploaded"}
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <Badge variant={special.is_active ? "secondary" : "outline"}>
                    {special.is_active ? "Active" : "Inactive"}
                  </Badge>
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="icon" variant="ghost" onClick={() => setEditing(special)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setConfirmDelete(special)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {(!specials || specials.length === 0) && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                  No video cards created yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={editing !== null} onOpenChange={v => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add Video Special" : "Edit Video Special"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="space-y-2">
              <Label>Featured Dish *</Label>
              <Select value={form.dish_id} onValueChange={v => setForm({ ...form, dish_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a dish..." />
                </SelectTrigger>
                <SelectContent>
                  {dishes?.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} {!d.is_active && "(Inactive)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Video File (MP4) *</Label>
                <MediaField 
                  value={form.video_url} 
                  onChange={v => setForm({ ...form, video_url: v })} 
                  allowUrlInput
                  kind="video"
                  posterUrl={form.poster_url}
                />
              </div>
              <div className="space-y-2">
                <Label>Poster Image *</Label>
                <MediaField 
                  value={form.poster_url} 
                  onChange={v => setForm({ ...form, poster_url: v })} 
                  allowUrlInput
                  kind="image"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Sales Description (Optional)</Label>
              <Textarea 
                placeholder="e.g. Freshly grilled, seasoned with our secret spice mix..." 
                value={form.sales_description}
                onChange={e => setForm({ ...form, sales_description: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch 
                checked={form.is_active} 
                onCheckedChange={v => setForm({ ...form, is_active: v })}
              />
              <Label>Active on Homepage</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : "Save Card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={v => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete video card?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the video card for "{confirmDelete?.dish?.name}". The actual dish remains in the catalog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
